// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  dateLabelFromTimestamp,
} from "./flight-display-model.js";
import {
  supportedMonitorByMakeModel,
  supportedMonitorLabel,
  supportedMonitorMakes,
  supportedMonitorModelsForMake,
} from "./supported-monitors.js";

// macOS folder droppings: AppleDouble resource forks ("._Flt0336.csv"),
// __MACOSX archive folders, and .DS_Store. A Mac-sourced folder carries one
// "._" sibling per real file, so without this filter half the batch is junk
// POSTs that the server ignores one by one.
export function macJunkUploadFile(file) {
  const path = String(file?.webkitRelativePath || file?.name || "");
  const name = path.split("/").pop() ?? "";
  return name.startsWith("._") || name === ".DS_Store" || path.split("/").includes("__MACOSX");
}

export function supportedUploadFile(file) {
  return /\.(ald|csv|jpi|zip)$/i.test(file?.name ?? "") && !macJunkUploadFile(file);
}

export function uploadFileDisplayName(file) {
  return file?.webkitRelativePath || file?.name || "selected file";
}

export function uploadSelectionFiles(fileLists = []) {
  return fileLists
    .flatMap((files) => [...(files ?? [])])
    .filter(supportedUploadFile)
    .sort((left, right) => uploadFileDisplayName(left).localeCompare(uploadFileDisplayName(right)));
}

// Best guess at when a log's flight happened, from the filename alone. Monitor
// exports stamp the date into the name (EI CGR "log_260614_084738_KPRS.csv",
// Garmin "log_240118_101530.csv", JPI/Dynon "2024-01-18...", "20240118..."),
// which is the only flight-time signal available before the file is uploaded
// and parsed. Returns null when nothing date-shaped is in the name.
export function uploadFileNameFlightTime(file) {
  const name = String(file?.name ?? "").replace(/\.[a-z0-9]+$/i, "");
  const isoish = name.match(/(20\d{2})[-_]?(0[1-9]|1[0-2])[-_]?(0[1-9]|[12]\d|3[01])/);
  if (isoish) {
    const stamp = Date.UTC(Number(isoish[1]), Number(isoish[2]) - 1, Number(isoish[3]));
    if (Number.isFinite(stamp)) return stamp;
  }
  // yymmdd, the EI / Garmin "log_" convention. Anchored on the separator so a
  // bare 6-digit sequence number can't masquerade as a date.
  const short = name.match(/(?:^|[^0-9])(\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])(?:[^0-9]|$)/);
  if (short) {
    const stamp = Date.UTC(2000 + Number(short[1]), Number(short[2]) - 1, Number(short[3]));
    if (Number.isFinite(stamp)) return stamp;
  }
  return null;
}

/**
 * Order a batch so the flights an account will still have access to after its
 * plan limit applies are processed first. Plan slots go to the most recent
 * flights (src/core/plan-access.ts), so newest-first means the user watches the
 * flights they keep come in before the ones that land locked. Files whose names
 * carry a date sort by it; the rest fall back to reverse name order, which is
 * chronological for every monitor export we support.
 */
export function uploadFilesNewestFlightFirst(files = []) {
  return [...files].sort((left, right) => {
    const leftTime = uploadFileNameFlightTime(left);
    const rightTime = uploadFileNameFlightTime(right);
    if (leftTime != null && rightTime != null && leftTime !== rightTime) return rightTime - leftTime;
    return uploadFileDisplayName(right).localeCompare(uploadFileDisplayName(left));
  });
}

/**
 * Does this account's plan actually withhold flights? Only an enforced metered
 * plan with a finite flight limit can leave part of a batch locked; everyone
 * else keeps the plain name order.
 */
export function accountWithholdsFlights(planPayload) {
  if (String(planPayload?.plan_enforcement_mode ?? "") !== "enforced") return false;
  if (String(planPayload?.effective_plan?.mode ?? "") !== "metered") return false;
  const limits = planPayload?.effective_plan?.entitlements ?? planPayload?.limits ?? {};
  const finite = (value) => value != null && Number.isFinite(Number(value));
  return finite(limits.maxTotalFlights) || finite(limits.maxFlightsPerAircraft);
}

/**
 * Copy for the plan-capacity upload gates. The server enforces the same caps
 * (403 reason "plan_flight_limit" on POST /api/uploads); these strings are the
 * friendly client-side version shown before anything is sent.
 */
export function uploadAtFlightLimitMessage(capacity = {}) {
  const plan = String(capacity?.planLabel ?? "").trim();
  const cap = Number(capacity?.limit);
  return `Your ${plan || "current"} plan is at its flight limit${Number.isFinite(cap) && cap > 0 ? ` (${cap})` : ""}. `
    + "Archive flights from the Archive tab in My Account, or upgrade your plan, to upload more.";
}

/**
 * Counter line for the over-capacity file picker: how many of the selection
 * are checked vs how much room the plan has. The checked count can never
 * exceed `remaining` (the dialog disables further checkboxes at the cap).
 */
export function uploadCapacityCounterText(checkedCount, totalCount, remaining) {
  const room = Math.max(0, Number(remaining) || 0);
  return `${checkedCount} of ${totalCount} files selected. Your plan has room for ${room} more flight${room === 1 ? "" : "s"}.`;
}

/**
 * Model for the over-capacity picker: newest flights pre-checked up to the
 * plan's remaining room, so the default choice matches what the plan would
 * keep anyway. Returns ordered files plus the pre-checked index set.
 */
export function uploadCapacitySelectionModel(files = [], remaining = 0) {
  const room = Math.max(0, Math.floor(Number(remaining) || 0));
  const ordered = uploadFilesNewestFlightFirst(files);
  return {
    files: ordered,
    remaining: room,
    preChecked: new Set(ordered.slice(0, room).map((_, index) => index)),
  };
}

export const MAX_UPLOAD_FILES_PER_SESSION = 100;

export function tooManyUploadFilesMessage(count, limit = MAX_UPLOAD_FILES_PER_SESSION) {
  return `Uploads are limited to ${limit} files at a time. You selected ${count} — remove ${count - limit} and upload the rest in another batch.`;
}

export function uploadSelectionSummary({
  selectedCount = 0,
  supportedCount = 0,
  folderSelected = false,
} = {}) {
  if (!selectedCount) return `Supported source files: \`.ald\`, \`.csv\`, \`.jpi\`, and \`.zip\`. 75 MB max per file, ${MAX_UPLOAD_FILES_PER_SESSION} files max per upload.`;
  const skipped = selectedCount - supportedCount;
  const source = folderSelected ? "folder" : "file picker";
  const summary = `${supportedCount} supported log${supportedCount === 1 ? "" : "s"} selected from ${source}${skipped ? `; ${skipped} other file${skipped === 1 ? "" : "s"} ignored` : ""}.`;
  if (supportedCount > MAX_UPLOAD_FILES_PER_SESSION) {
    return `${summary} ${tooManyUploadFilesMessage(supportedCount)}`;
  }
  return summary;
}

export function splitUploadMakeModel(value) {
  const text = String(value ?? "").trim().replace(/\s+/g, " ");
  if (!text) return { make: "", model: "" };
  const [make, ...modelParts] = text.split(" ");
  if (!modelParts.length) return { make: "", model: make };
  return { make, model: modelParts.join(" ") };
}

export function monitorSelectionFromText(value) {
  const text = String(value ?? "").trim().replace(/\s+/g, " ");
  if (!text) return { make: "", model: "" };
  const direct = supportedMonitorMakes()
    .flatMap((make) => supportedMonitorModelsForMake(make).map((model) => ({ make, model })))
    .find((monitor) => supportedMonitorLabel(monitor).toLowerCase() === text.toLowerCase());
  if (direct) return direct;
  const make = supportedMonitorMakes().find((candidate) => text.toLowerCase().startsWith(`${candidate.toLowerCase()} `));
  if (make) return { make, model: text.slice(make.length).trim() };
  return splitUploadMakeModel(text);
}

export function selectedMonitorFromSelectValues(make, model) {
  return supportedMonitorByMakeModel(make, model)
    ?? { make: String(make ?? "").trim(), model: String(model ?? "").trim() };
}

export function uploadAircraftMetadataSearchParams({
  fileName,
  filename,
  relativePath = "",
  targetAircraft = "",
  targetAircraftId = "",
  creatingNew = false,
  make = "",
  model = "",
  engine = "",
  propellerType = "",
  monitor = null,
  includeShortFlights = false,
  limitAircraftModel = (value) => value,
} = {}) {
  const uploadFileName = fileName ?? filename ?? "";
  const params = new URLSearchParams({
    filename: uploadFileName,
  });
  const cleanRelativePath = String(relativePath ?? "").trim();
  if (cleanRelativePath) params.set("relativePath", cleanRelativePath);
  const cleanTargetAircraft = String(targetAircraft ?? "").trim();
  if (cleanTargetAircraft) params.set("tailNumber", cleanTargetAircraft);
  const cleanTargetAircraftId = String(targetAircraftId ?? "").trim();
  if (cleanTargetAircraftId && !creatingNew) params.set("aircraftId", cleanTargetAircraftId);
  // Short-flight upload gate opt-in applies to both new and existing aircraft, so
  // set it before the existing-aircraft early return below.
  if (includeShortFlights) params.set("includeShortFlights", "1");
  if (!creatingNew) return params;

  const engineParts = splitUploadMakeModel(engine);
  const selectedMonitor = monitor ?? { make: "", model: "" };
  const monitorType = supportedMonitorLabel(selectedMonitor);
  const optionalValues = {
    make,
    model: limitAircraftModel(model),
    engineMake: engineParts.make,
    engineModel: engineParts.model,
    propellerType,
    monitorMake: selectedMonitor.make,
    monitorModel: selectedMonitor.model,
    monitorType,
  };
  Object.entries(optionalValues).forEach(([key, value]) => {
    const text = String(value ?? "").trim();
    if (text) params.set(key, text);
  });
  return params;
}

export function duplicateUploadMessage(payload, fallbackFileName = "this log", {
  formatUploadedAt = (timestamp) => new Date(timestamp).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }),
} = {}) {
  const upload = payload?.duplicateUpload ?? {};
  const name = upload.original_filename || fallbackFileName;
  const uploadedAt = upload.uploaded_at ? formatUploadedAt(upload.uploaded_at) : "";
  const suffix = uploadedAt ? ` on ${uploadedAt}` : "";
  const archivedNote = duplicateUploadHasArchivedFlight(payload)
    ? " Its flight is archived. Restore it from the Archive tab in My Account instead of uploading it again."
    : "";
  return `This log has already been uploaded as ${name}${suffix}.${archivedNote}`;
}

// The server names the archived flights that still own a duplicate log; without this the
// duplicate reads as if the log never linked.
export function duplicateUploadHasArchivedFlight(payload) {
  return Array.isArray(payload?.archivedFlights) && payload.archivedFlights.length > 0;
}

export function uploadDuplicateSummary(duplicates) {
  if (!duplicates.length) return "";
  if (duplicates.length === 1) return duplicateUploadMessage(duplicates[0].payload, duplicates[0].fileName);
  const archivedCount = duplicates.filter((duplicate) => duplicateUploadHasArchivedFlight(duplicate.payload)).length;
  const base = `${duplicates.length} duplicate logs were skipped.`;
  if (!archivedCount) return base;
  if (archivedCount === duplicates.length) {
    return `${base} Their flights are archived. Restore them from the Archive tab in My Account instead of uploading them again.`;
  }
  return `${base} ${archivedCount} of them belong to archived flights. Restore those from the Archive tab in My Account.`;
}

export function uploadBatchSummary({ uploaded, duplicates, failed }) {
  const parts = [];
  if (uploaded) parts.push(`${uploaded} uploaded`);
  if (duplicates) parts.push(`${duplicates} duplicate${duplicates === 1 ? "" : "s"} skipped`);
  if (failed) parts.push(`${failed} failed`);
  return parts.length ? parts.join(", ") : "No logs uploaded";
}

export function shortFlightsSkippedSummary(count) {
  const flights = count === 1 ? "1 flight was" : `${count} flights were`;
  const pronoun = count === 1 ? "it" : "them";
  return `Uploaded. ${flights} skipped because ${count === 1 ? "it was" : "they were"} shorter than 10 minutes. `
    + `Use "Process Short Flights" on the uploads page to analyze ${pronoun}.`;
}

export function noEngineDataRefusedSummary(count) {
  const subject = count === 1 ? "This log doesn't" : `${count} logs don't`;
  const looksLike = count === 1 ? "It looks like an airframe or air-data log" : "They look like airframe or air-data logs";
  const nextStep = count === 1
    ? "Export the engine log from your monitor and upload that instead."
    : "Export the engine logs from your monitor and upload those instead.";
  return `${subject} contain engine data (EGT, CHT, oil temperature or pressure, fuel flow, RPM). ${looksLike}. ${nextStep}`;
}

export function uploadIngestStatusLabel(job) {
  const step = uploadIngestStepSentenceLabel(job?.current_step || job?.status || "queued");
  const completed = Number(job?.progress_completed ?? 0);
  const total = Number(job?.progress_total ?? 0);
  if (total > 0 && uploadIngestStepShowsProgress(job?.current_step)) return `Processing upload: ${step} (${Math.min(completed, total)}/${total})...`;
  return `Processing upload: ${step}...`;
}

export function formatUploadByteSize(value) {
  if (value === null || value === undefined || value === "") return "--";
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return "--";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let amount = bytes / 1024;
  let unit = units[0];
  for (let index = 1; index < units.length && amount >= 1024; index += 1) {
    amount /= 1024;
    unit = units[index];
  }
  return `${amount >= 10 ? amount.toFixed(0) : amount.toFixed(1)} ${unit}`;
}

export function uploadStatusLabel(status) {
  return String(status ?? "unknown").replace(/[-_]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function uploadIngestStepSentenceLabel(status) {
  if (String(status ?? "") === "waiting-for-aircraft-lock") return "waiting";
  return String(status ?? "unknown").replace(/[-_]+/g, " ");
}

function uploadIngestStepTitleLabel(status) {
  if (String(status ?? "") === "waiting-for-aircraft-lock") return "Waiting";
  return uploadStatusLabel(status);
}

function uploadIngestStepShowsProgress(status) {
  return String(status ?? "") !== "waiting-for-aircraft-lock";
}

export function uploadStatusClass(status) {
  const normalized = String(status ?? "unknown").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return normalized || "unknown";
}

export function uploadIsEmpty(upload) {
  return Number(upload?.byte_size ?? 0) <= 0;
}

export function uploadDisplayStatus(upload) {
  if (uploadIsEmpty(upload)) return "empty";
  // upload_status "completed" only means the BYTES are stored. While the
  // latest ingest job is still queued or running, the row is not done —
  // surface the pipeline state so the uploads page never says "Completed"
  // next to a "Queued" job.
  const jobStatus = String(upload?.latest_ingest_job?.status ?? "").toLowerCase();
  if (jobStatus === "queued") return "queued";
  if (jobStatus === "processing") return "processing";
  return upload?.upload_status ?? "unknown";
}

export function uploadDisplayError(upload, job = upload?.latest_ingest_job ?? null) {
  if (uploadIsEmpty(upload)) return upload?.error_message || job?.error_message || "Source file is empty; ingest was skipped.";
  return job?.error_message || upload?.error_message || "";
}

export function uploadJobProgressLabel(job, upload = null) {
  if (uploadIsEmpty(upload)) return "Skipped";
  if (!job) return "No ingest job";
  // While a job waits for a worker slot its current_step flips between
  // "queued" and "waiting" (the server's queue heartbeat feeding the client
  // stall detector) and its 0/N progress is meaningless — show the stable
  // "Queued" label instead of "Waiting 0/1" / "Queued 0/1".
  if (String(job.status ?? "").toLowerCase() === "queued") return "Queued";
  const step = uploadIngestStepTitleLabel(job.current_step || job.status);
  const completed = Number(job.progress_completed ?? 0);
  const total = Number(job.progress_total ?? 0);
  const progress = total > 0 && uploadIngestStepShowsProgress(job.current_step)
    ? ` ${Math.min(completed, total)}/${total}`
    : "";
  return `${step}${progress}`;
}

export function uploadFileNameFromPath(sourcePath) {
  return String(sourcePath ?? "").split("/").filter(Boolean).pop() ?? "";
}

export function uploadFlightSummary(upload) {
  if (upload?.cgr_twin) {
    const twin = upload.cgr_twin;
    // The owning twin flight sits in the Archive tab: the recordings are linked, not missing.
    if (twin.flightId && twin.archived) {
      return {
        label: "Archived twin flight",
        detail: "Restore it from the Archive tab in My Account",
      };
    }
    return {
      label: twin.flightId ? "Twin flight" : "Waiting for engine log",
      detail: twin.complete ? "Both engine recordings linked" : "Waiting for the other engine recording",
    };
  }
  const pairing = upload?.cgr_pairing ?? null;
  if (pairing?.status === "paired") {
    const role = pairing.role === "auxiliary" ? "auxiliary" : "primary";
    if (pairing.alignment === "unaligned") {
      return {
        label: "Combined (time-unaligned)",
        detail: "Clocks not aligned — 30C channels display-only",
      };
    }
    const counterpart = upload?.paired_upload?.original_filename ?? uploadFileNameFromPath(pairing.pairedPath);
    const label = role === "auxiliary" ? "Paired with 30P" : "1 flight";
    const detail = role === "auxiliary"
      ? `Part of ${pairing.flightTitle ?? "paired CGR flight"}`
      : (counterpart ? `Paired with ${counterpart}` : "CGR-30P + 30C raw files");
    return { label, detail };
  }
  if (pairing?.status === "unpaired") {
    if (pairing.role === "auxiliary") return { label: "Waiting for 30P", detail: "No matching CGR-30P yet" };
    return { label: `${upload?.supported_flight_count ?? 0}/${upload?.flight_count ?? 0}`, detail: "No matching CGR-30C yet" };
  }
  return { label: `${upload?.supported_flight_count ?? 0}/${upload?.flight_count ?? 0}`, detail: "" };
}

export function uploadFlightSummaryDetailClass(upload) {
  const pairing = upload?.cgr_pairing ?? null;
  if (pairing?.status === "paired" && pairing.alignment !== "unaligned") return "is-paired";
  if (pairing?.status === "paired" || pairing?.status === "unpaired") return "is-unpaired";
  return "";
}

export function uploadHasActiveJob(upload) {
  if (uploadIsEmpty(upload)) return false;
  const status = String(upload?.latest_ingest_job?.status ?? "").toLowerCase();
  return status === "queued" || status === "processing";
}

export function uploadReprocessActionLabel(upload, {
  reprocessPending = false,
  reprocessActive = false,
} = {}) {
  if (uploadIsEmpty(upload)) return "Empty";
  const jobStatus = String(upload?.latest_ingest_job?.status ?? "").toLowerCase();
  if (reprocessActive || jobStatus === "processing") return "Processing";
  if (reprocessPending || jobStatus === "queued") return "Queued";
  return "Reprocess";
}

export function uploadCanReprocess(upload, {
  readOnly = false,
  reprocessPending = false,
  deletePending = false,
} = {}) {
  return Boolean(upload?.id)
    && !readOnly
    && !uploadIsEmpty(upload)
    && !uploadHasActiveJob(upload)
    && !reprocessPending
    && !deletePending;
}

export function uploadDateLabel(upload) {
  return dateLabelFromTimestamp(upload?.uploaded_at) ?? "Date unavailable";
}

export function shortUploadHash(upload) {
  const hash = String(upload?.sha256 ?? "").replace(/^sha256:/i, "");
  return hash ? hash.slice(0, 12) : "--";
}
